import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCatalog, getUploadUrl, uploadFile, publish } from '../api';
import { validateImage } from '../validation';
import { processImage } from '../utils/imageProcessor';
import { TextBandEditor } from '../components/TextBandEditor';
import type { FileToUpload, Catalog, LANGUAGES, ManifestItem, PackManifest } from '../types';

interface TemplateWithTitle extends FileToUpload {
  title: string;
  locale: string;
  previewUrl?: string; // For client-side preview
  type: 'image' | 'video';
}

const LANGUAGE_NAMES: Record<string, string> = {
  'en': 'English',
  'hi': 'Hindi',
  'ta': 'Tamil',
  'te': 'Telugu',
  'kn': 'Kannada',
  'ml': 'Malayalam',
  'mr': 'Marathi',
  'bn': 'Bengali',
  'gu': 'Gujarati',
  'pa': 'Punjabi'
};

export const Upload: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);

  // Category selection - Two-step process
  const [selectedCategoryType, setSelectedCategoryType] = useState<string>('');
  const [isNewCategoryType, setIsNewCategoryType] = useState(false);
  const [customCategoryType, setCustomCategoryType] = useState('');
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  // New category fields
  const [newBucketId, setNewBucketId] = useState('');

  // Global language selector for all uploads
  const [globalLanguage, setGlobalLanguage] = useState<typeof LANGUAGES[number]>('en');

  // Existing templates (for merging when adding to existing pack)
  const [existingTemplates, setExistingTemplates] = useState<ManifestItem[]>([]);

  const [images, setImages] = useState<TemplateWithTitle[]>([]);
  const [videos, setVideos] = useState<TemplateWithTitle[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActiveImage, setDragActiveImage] = useState(false);
  const [dragActiveVideo, setDragActiveVideo] = useState(false);

  // Tab selection state
  const [activeTab, setActiveTab] = useState<'image' | 'video'>('image');

  // Text band editor state
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [editingVideoIndex, setEditingVideoIndex] = useState<number | null>(null);

  // Preview state
  const [previewingVideoIndex, setPreviewingVideoIndex] = useState<number | null>(null);


  // Load catalog on mount and detect version
  useEffect(() => {
    loadCatalog();
  }, []);

  // Pre-fill category from URL params
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && catalog) {
      const pack = catalog.packs.find(p => p.id === categoryParam);
      if (pack) {
        setSelectedCategoryType(pack.category);
        setSelectedCategoryId(categoryParam);
        setIsNewCategory(false);
      }
    }
  }, [searchParams, catalog]);

  // Note: We don't merge versions anymore. Each version stays pure.
  // The manifest_all.json is created on backend after publishing.
  useEffect(() => {
    // Always keep existing templates empty - we don't merge versions in upload
    setExistingTemplates([]);
  }, [isNewCategory, selectedCategoryId, catalog]);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const data = await getCatalog();
      setCatalog(data);
    } catch (err) {
      console.error('Failed to load catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActiveImage(true);
    } else if (e.type === 'dragleave') {
      setDragActiveImage(false);
    }
  }, []);

  const handleVideoDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActiveVideo(true);
    } else if (e.type === 'dragleave') {
      setDragActiveVideo(false);
    }
  }, []);

  // Helper: Generate title from filename
  const generateTitle = (filename: string): string => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.(webp|jpg|jpeg|png|mp4|mov|avi|mkv)$/i, '');
    // Replace underscores and hyphens with spaces
    const withSpaces = nameWithoutExt.replace(/[_-]/g, ' ');
    // Capitalize first letter of each word
    return withSpaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper: Sanitize filename for ID (lowercase, alphanumeric + underscore only)
  const sanitizeForId = (filename: string): string => {
    return filename
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const processImageFiles = async (fileList: FileList) => {
    // Check if adding these files would exceed 20 limit
    if (images.length + fileList.length > 20) {
      alert(`Maximum 20 images allowed. You currently have ${images.length} images. Can only add ${20 - images.length} more.`);
      return;
    }

    const newImages: TemplateWithTitle[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!file || (!file.type.includes('image') && !file.name.match(/\.(webp|jpg|jpeg|png)$/i))) {
        continue; // Skip non-image files
      }

      // Validate image (must be 1080x1080)
      const validation = await validateImage(file, 'background');

      const packId = isNewCategory ? newBucketId : selectedCategoryId;
      const baseName = file.name.replace(/\.(webp|jpg|jpeg|png)$/i, '');
      const sanitizedName = sanitizeForId(baseName);
      const path = `/packs/${packId}/v1/${sanitizedName}_full.webp`;

      // Create preview URL for displaying the image
      const previewUrl = URL.createObjectURL(file);

      newImages.push({
        file,
        path,
        contentType: 'image/webp',
        status: validation.valid ? 'pending' : 'error',
        error: validation.valid ? undefined : validation.errors.join(', '),
        title: generateTitle(file.name),
        locale: globalLanguage,
        previewUrl,
        type: 'image'
      });
    }

    setImages(prev => [...prev, ...newImages]);
  };

  const processVideoFiles = async (fileList: FileList) => {
    // Check if adding these files would exceed 20 limit
    if (videos.length + fileList.length > 20) {
      alert(`Maximum 20 videos allowed. You currently have ${videos.length} videos. Can only add ${20 - videos.length} more.`);
      return;
    }

    const newVideos: TemplateWithTitle[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!file || (!file.type.includes('video') && !file.name.match(/\.(mp4|mov|avi|mkv)$/i))) {
        continue; // Skip non-video files
      }

      const packId = isNewCategory ? newBucketId : selectedCategoryId;
      const baseName = file.name.replace(/\.(mp4|mov|avi|mkv)$/i, '');
      const sanitizedName = sanitizeForId(baseName);
      const path = `/packs/${packId}/v1/${sanitizedName}_full.mp4`;

      // Create preview URL for displaying the video
      const previewUrl = URL.createObjectURL(file);

      newVideos.push({
        file,
        path,
        contentType: 'video/mp4',
        status: 'pending',
        title: generateTitle(file.name),
        locale: globalLanguage,
        previewUrl,
        type: 'video'
      });
    }

    setVideos(prev => [...prev, ...newVideos]);
  };

  const handleImageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveImage(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processImageFiles(e.dataTransfer.files);
    }
  }, [selectedCategoryId, newBucketId, isNewCategory]);

  const handleVideoDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveVideo(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processVideoFiles(e.dataTransfer.files);
    }
  }, [selectedCategoryId, newBucketId, isNewCategory]);

  const handleImageFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processImageFiles(e.target.files);
    }
  };

  const handleVideoFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processVideoFiles(e.target.files);
    }
  };

  const handleUploadAndPublish = async () => {
    // Validate category selection
    const packId = isNewCategory ? newBucketId.trim() : selectedCategoryId;
    if (!packId) {
      alert('Please select or create a category first');
      return;
    }

    if (isNewCategory && !newBucketId.trim()) {
      alert('Please enter a title for the new category');
      return;
    }

    const pendingImages = images.filter(img => img.status === 'pending');
    const pendingVideos = videos.filter(vid => vid.status === 'pending');

    if (pendingImages.length === 0 && pendingVideos.length === 0) {
      alert('No valid templates to upload');
      return;
    }

    setUploading(true);

    try {
      const manifestItems: ManifestItem[] = [];

      // Get current version for existing packs
      let targetVersion = 1;
      if (!isNewCategory && catalog) {
        const existingPack = catalog.packs.find(p => p.id === packId);
        if (existingPack?.versionPath) {
          const versionMatch = existingPack.versionPath.match(/\/v(\d+)\//);
          const currentVersion = versionMatch && versionMatch[1] ? parseInt(versionMatch[1]) : 1;
          targetVersion = currentVersion + 1; // Increment version for existing packs
        }
      }

      // Upload all images and generate thumbnails
      for (const image of pendingImages) {
        setImages(prev =>
          prev.map(img =>
            img === image ? { ...img, status: 'uploading' } : img
          )
        );

        try {
          // Generate thumbnail
          const variants = await processImage(image.file);

          // Fix path to use correct version
          const correctedPath = image.path.replace(/\/v\d+\//, `/v${targetVersion}/`);

          // Upload full-size image
          const fullUrlData = await getUploadUrl(correctedPath, 'image/webp');
          await uploadFile(fullUrlData.putUrl, image.file, fullUrlData.headers);

          // Upload thumbnail
          const thumbPath = correctedPath.replace('_full.webp', '_thumb.webp');
          const thumbUrlData = await getUploadUrl(thumbPath, 'image/webp');
          await uploadFile(thumbUrlData.putUrl, variants.thumbnail, thumbUrlData.headers);

          // Create simple template JSON
          const templateId = image.path.match(/\/([^/]+)_full\.webp$/)?.[1] || '';
          const template = {
            id: templateId,
            size: { w: 1080, h: 1080 },
            bg: {
              type: 'image' as const,
              url: fullUrlData.publicUrl
            },
            layers: [],
            export: {
              format: 'webp' as const,
              watermark: false,
              dpi: 300
            }
          };

          // Upload template JSON
          const templatePath = correctedPath.replace('_full.webp', '_template.json');
          const templateBlob = new Blob([JSON.stringify(template, null, 2)], {
            type: 'application/json'
          });
          const templateUrlData = await getUploadUrl(templatePath, 'application/json');
          await uploadFile(templateUrlData.putUrl, templateBlob, templateUrlData.headers);

          // Add to manifest
          manifestItems.push({
            id: templateId,
            title: image.title,
            locale: image.locale,
            previewUrl: thumbUrlData.publicUrl,
            templateUrl: templateUrlData.publicUrl,
            version: targetVersion
          });

          setImages(prev =>
            prev.map(img =>
              img === image ? { ...img, status: 'success', publicUrl: fullUrlData.publicUrl } : img
            )
          );
        } catch (err) {
          setImages(prev =>
            prev.map(img =>
              img === image
                ? {
                    ...img,
                    status: 'error',
                    error: err instanceof Error ? err.message : 'Upload failed'
                  }
                : img
            )
          );
        }
      }

      // Upload all videos
      for (const video of pendingVideos) {
        setVideos(prev =>
          prev.map(vid =>
            vid === video ? { ...vid, status: 'uploading' } : vid
          )
        );

        try {
          // Fix path to use correct version
          const correctedPath = video.path.replace(/\/v\d+\//, `/v${targetVersion}/`);

          // Upload full video
          const fullUrlData = await getUploadUrl(correctedPath, 'video/mp4');
          await uploadFile(fullUrlData.putUrl, video.file, fullUrlData.headers);

          // For video thumbnails, we'll use a placeholder or first frame extraction in the future
          // For now, use the video URL directly as preview
          const thumbUrl = ''; // Placeholder - will generate video thumbnail later

          // Create simple video template JSON
          const templateId = correctedPath.match(/\/([^/]+)_full\.mp4$/)?.[1] || '';
          const template = {
            id: templateId,
            size: { w: 1080, h: 1920 }, // Vertical video format
            bg: {
              type: 'video' as const,
              url: fullUrlData.publicUrl
            },
            layers: [],
            export: {
              format: 'mp4' as const,
              watermark: false,
              dpi: 72
            }
          };

          // Upload template JSON
          const templatePath = correctedPath.replace('_full.mp4', '_template.json');
          const templateBlob = new Blob([JSON.stringify(template, null, 2)], {
            type: 'application/json'
          });
          const templateUrlData = await getUploadUrl(templatePath, 'application/json');
          await uploadFile(templateUrlData.putUrl, templateBlob, templateUrlData.headers);

          // Add to manifest
          manifestItems.push({
            id: templateId,
            title: video.title,
            locale: video.locale,
            previewUrl: thumbUrl || fullUrlData.publicUrl, // Use video URL if no thumb
            templateUrl: templateUrlData.publicUrl,
            version: targetVersion
          });

          setVideos(prev =>
            prev.map(vid =>
              vid === video ? { ...vid, status: 'success', publicUrl: fullUrlData.publicUrl } : vid
            )
          );
        } catch (err) {
          setVideos(prev =>
            prev.map(vid =>
              vid === video
                ? {
                    ...vid,
                    status: 'error',
                    error: err instanceof Error ? err.message : 'Upload failed'
                  }
                : vid
            )
          );
        }
      }

      // Create and publish manifest
      if (manifestItems.length > 0) {
        // Don't merge - each version contains only its own items
        const allItems = manifestItems;

        // Get current version for existing packs, or use 1 for new packs
        let targetVersion = 1;
        if (!isNewCategory && catalog) {
          const existingPack = catalog.packs.find(p => p.id === packId);
          if (existingPack?.versionPath) {
            const versionMatch = existingPack.versionPath.match(/\/v(\d+)\//);
            const currentVersion = versionMatch && versionMatch[1] ? parseInt(versionMatch[1]) : 1;
            targetVersion = currentVersion + 1; // Increment version for existing packs
          }
        }

        const manifest: PackManifest = {
          id: packId,
          version: targetVersion,
          items: allItems
        };

        const finalCategoryType = isNewCategory
          ? (isNewCategoryType ? customCategoryType : selectedCategoryType)
          : catalog?.packs.find(p => p.id === packId)?.category || 'GoodMorning';

        const catalogPack = {
          packId: packId,
          title: isNewCategory ? newBucketId : catalog?.packs.find(p => p.id === packId)?.title || packId,
          languages: isNewCategory ? [globalLanguage] : catalog?.packs.find(p => p.id === packId)?.languages || ['en'],
          category: finalCategoryType as any, // Allow any string for custom types
          thumbUrl: manifestItems[0]?.previewUrl || ''
        };

        await publish(packId, targetVersion, manifest, catalogPack);

        const uploadedCount = manifestItems.length;

        alert(`✅ Successfully uploaded ${uploadedCount} ${uploadedCount === 1 ? 'item' : 'items'}!`);

        // Reload catalog
        await loadCatalog();

        // Clear form
        setImages([]);
        setVideos([]);
        setSelectedCategoryType('');
        setIsNewCategoryType(false);
        setCustomCategoryType('');
        setSelectedCategoryId('');
        setNewBucketId('');
        setIsNewCategory(false);
      }
    } catch (err) {
      alert(`Failed to publish: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const updateImageTitle = (index: number, title: string) => {
    setImages(prev =>
      prev.map((img, i) => (i === index ? { ...img, title } : img))
    );
  };

  const updateImageLocale = (index: number, locale: string) => {
    setImages(prev =>
      prev.map((img, i) => (i === index ? { ...img, locale } : img))
    );
  };

  const removeImage = (index: number) => {
    setImages(prev => {
      // Clean up preview URL to prevent memory leak
      const image = prev[index];
      if (image?.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateVideoTitle = (index: number, title: string) => {
    setVideos(prev =>
      prev.map((vid, i) => (i === index ? { ...vid, title } : vid))
    );
  };

  const updateVideoLocale = (index: number, locale: string) => {
    setVideos(prev =>
      prev.map((vid, i) => (i === index ? { ...vid, locale } : vid))
    );
  };

  const removeVideo = (index: number) => {
    setVideos(prev => {
      // Clean up preview URL to prevent memory leak
      const video = prev[index];
      if (video?.previewUrl) {
        URL.revokeObjectURL(video.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleTextOverlayComplete = (overlayedImage: File) => {
    if (editingImageIndex === null) return;

    setImages(prev =>
      prev.map((img, i) => {
        if (i === editingImageIndex) {
          // Clean up old preview URL
          if (img.previewUrl) {
            URL.revokeObjectURL(img.previewUrl);
          }
          // Create new preview URL for overlayed image
          const newPreviewUrl = URL.createObjectURL(overlayedImage);
          return {
            ...img,
            file: overlayedImage,
            previewUrl: newPreviewUrl
          };
        }
        return img;
      })
    );
    setEditingImageIndex(null);
  };

  const handleCancelTextEditor = () => {
    setEditingImageIndex(null);
  };

  const handleVideoTextOverlayComplete = (overlayedVideo: File) => {
    if (editingVideoIndex === null) return;

    setVideos(prev =>
      prev.map((vid, i) => {
        if (i === editingVideoIndex) {
          // Clean up old preview URL
          if (vid.previewUrl) {
            URL.revokeObjectURL(vid.previewUrl);
          }
          // Create new preview URL for overlayed video
          const newPreviewUrl = URL.createObjectURL(overlayedVideo);
          return {
            ...vid,
            file: overlayedVideo,
            previewUrl: newPreviewUrl
          };
        }
        return vid;
      })
    );
    setEditingVideoIndex(null);
  };

  const handleCancelVideoTextEditor = () => {
    setEditingVideoIndex(null);
  };

  const clearAll = () => {
    // Clean up all preview URLs
    images.forEach(image => {
      if (image.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
    });
    videos.forEach(video => {
      if (video.previewUrl) {
        URL.revokeObjectURL(video.previewUrl);
      }
    });
    setImages([]);
    setVideos([]);
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach(image => {
        if (image.previewUrl) {
          URL.revokeObjectURL(image.previewUrl);
        }
      });
      videos.forEach(video => {
        if (video.previewUrl) {
          URL.revokeObjectURL(video.previewUrl);
        }
      });
    };
  }, [images, videos]);

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h2>Upload Templates</h2>
        </div>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2>Upload Templates</h2>
        <p>Upload images to create new templates for your categories</p>
      </div>

      {/* Category Selection */}
      <div className="card">
        <h3>1. Select or Create Category</h3>

        {/* Step 1: Select Category Type */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
            Category Type
          </label>
          <select
            value={isNewCategoryType ? '__CREATE_NEW_TYPE__' : selectedCategoryType}
            onChange={(e) => {
              if (e.target.value === '__CREATE_NEW_TYPE__') {
                setIsNewCategoryType(true);
                setSelectedCategoryType('');
                setCustomCategoryType('');
                setSelectedCategoryId('');
                setIsNewCategory(false);
                setNewBucketId('');
              } else {
                setIsNewCategoryType(false);
                setSelectedCategoryType(e.target.value);
                setCustomCategoryType('');
                setSelectedCategoryId('');
                setIsNewCategory(false);
                setNewBucketId('');
              }
            }}
            style={{ width: '100%', padding: '10px', fontSize: '14px' }}
          >
            <option value="">-- Select a category type --</option>
            {/* Show all unique categories from catalog */}
            {Array.from(new Set(catalog?.packs.map(p => p.category) || [])).sort().map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
            <option value="__CREATE_NEW_TYPE__">+ Create New Type</option>
          </select>
        </div>

        {/* Custom Category Type Input */}
        {isNewCategoryType && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
              Custom Category Type <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={customCategoryType}
              onChange={(e) => {
                // Capitalize first letter, remove special chars except spaces
                const cleaned = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                setCustomCategoryType(cleaned);
              }}
              placeholder="e.g., Birthday, Anniversary, Greetings"
              style={{ width: '100%', padding: '10px', fontSize: '14px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              Letters, numbers, and spaces only
            </div>
          </div>
        )}

        {/* Step 2: Select or Create Bucket */}
        {(selectedCategoryType || (isNewCategoryType && customCategoryType)) && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
              Select Bucket or Create New
            </label>
            <select
              value={isNewCategory ? '__CREATE_NEW__' : selectedCategoryId}
              onChange={(e) => {
                if (e.target.value === '__CREATE_NEW__') {
                  setIsNewCategory(true);
                  setSelectedCategoryId('');
                  setNewBucketId('');
                } else {
                  setIsNewCategory(false);
                  setSelectedCategoryId(e.target.value);
                  setNewBucketId('');
                }
              }}
              style={{ width: '100%', padding: '10px', fontSize: '14px' }}
            >
              <option value="">-- Select a bucket --</option>
              {!isNewCategoryType && catalog?.packs
                .filter(pack => pack.category === selectedCategoryType)
                .map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.title} ({pack.id})
                  </option>
                ))}
              <option value="__CREATE_NEW__">+ Create New Bucket ID</option>
            </select>
          </div>
        )}

        {isNewCategory && (selectedCategoryType || (isNewCategoryType && customCategoryType)) && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
              Bucket ID <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={newBucketId}
              onChange={(e) => {
                // Only allow lowercase letters, numbers, and underscores
                const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setNewBucketId(sanitized);
              }}
              placeholder="e.g., goodmorning_jan26"
              style={{ width: '100%', padding: '10px', fontSize: '14px' }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              Lowercase letters, numbers, and underscores only. Type: <strong>{isNewCategoryType ? customCategoryType : selectedCategoryType}</strong>
            </div>
          </div>
        )}

        {(selectedCategoryId || (isNewCategory && newBucketId)) && !isNewCategory && existingTemplates.length > 0 && (
          <div className="alert alert-info" style={{ marginTop: '20px' }}>
            <strong>Existing Templates:</strong> {existingTemplates.length} templates in this bucket
            <br />
            <span style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'inline-block' }}>
              💡 New uploads will be added to existing templates
            </span>
          </div>
        )}
      </div>

      {/* Template Upload with Tabs */}
      <div className="card">
        <h3>2. Upload Media</h3>

        {/* Global Language Selector */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
            Default Language (applies to all uploads)
          </label>
          <select
            value={globalLanguage}
            onChange={(e) => {
              const newLang = e.target.value as typeof LANGUAGES[number];
              setGlobalLanguage(newLang);
              // Update all pending images to use new language
              setImages(prev => prev.map(img => ({ ...img, locale: newLang })));
              setVideos(prev => prev.map(vid => ({ ...vid, locale: newLang })));
            }}
            style={{ width: '300px', padding: '10px', fontSize: '14px' }}
          >
            {(['en', 'hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'] as const).map(lang => (
              <option key={lang} value={lang}>{LANGUAGE_NAMES[lang]}</option>
            ))}
          </select>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
            This will be applied to all uploaded media. You can change individual languages below.
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
          <button
            onClick={() => setActiveTab('image')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'image' ? '2px solid #2383E2' : '2px solid transparent',
              color: activeTab === 'image' ? '#2383E2' : '#666',
              fontWeight: activeTab === 'image' ? 600 : 400,
              fontSize: '14px'
            }}
          >
            🖼️ Images {images.length > 0 && `(${images.length})`}
          </button>
          <button
            onClick={() => setActiveTab('video')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'video' ? '2px solid #2383E2' : '2px solid transparent',
              color: activeTab === 'video' ? '#2383E2' : '#666',
              fontWeight: activeTab === 'video' ? 600 : 400,
              fontSize: '14px'
            }}
          >
            🎥 Videos {videos.length > 0 && `(${videos.length})`}
          </button>
        </div>

        {/* Image Upload Tab */}
        {activeTab === 'image' && (
          <>
            <div
              className={`drop-zone ${dragActiveImage ? 'active' : ''}`}
              onDragEnter={handleImageDrag}
              onDragLeave={handleImageDrag}
              onDragOver={handleImageDrag}
              onDrop={handleImageDrop}
              onClick={() => document.getElementById('image-file-input')?.click()}
            >
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</p>
              <p>Drag and drop images here, or click to select</p>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                Square images only (1080×1080 px) • WEBP, JPG, PNG • Max 20 images
              </p>
            </div>
            <input
              id="image-file-input"
              type="file"
              multiple
              onChange={handleImageFileInput}
              style={{ display: 'none' }}
              accept=".webp,.jpg,.jpeg,.png"
            />

            {images.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Images ({images.length})</h4>
                {images.map((image, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  backgroundColor: image.status === 'error' ? '#fff5f5' : '#fff'
                }}
              >
                {/* Image Preview */}
                {image.previewUrl && (
                  <div
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <img
                      src={image.previewUrl}
                      alt={image.file.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    {image.file.name} • {(image.file.size / 1024).toFixed(1)} KB
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                    <input
                      type="text"
                      value={image.title}
                      onChange={(e) => updateImageTitle(index, e.target.value)}
                      placeholder="Title"
                      disabled={image.status === 'uploading' || image.status === 'success'}
                      style={{ fontSize: '14px' }}
                    />
                    <select
                      value={image.locale}
                      onChange={(e) => updateImageLocale(index, e.target.value)}
                      disabled={image.status === 'uploading' || image.status === 'success'}
                      style={{ fontSize: '14px' }}
                    >
                      {(['en', 'hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'] as const).map(lang => (
                        <option key={lang} value={lang}>{LANGUAGE_NAMES[lang]}</option>
                      ))}
                    </select>
                  </div>
                  {!image.error && image.status !== 'uploading' && image.status !== 'success' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingImageIndex(index)}
                      style={{ fontSize: '12px', padding: '6px 10px', marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                      </svg>
                      Add Band
                    </button>
                  )}
                  {image.error && (
                    <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px' }}>
                      ❌ {image.error}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={`badge badge-${
                    image.status === 'success' ? 'success' :
                    image.status === 'error' ? 'error' :
                    image.status === 'uploading' ? 'warning' : 'success'
                  }`}>
                    {image.status}
                  </span>
                  {image.status !== 'uploading' && image.status !== 'success' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => removeImage(index)}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Video Upload Tab */}
        {activeTab === 'video' && (
          <>
            <div
              className={`drop-zone ${dragActiveVideo ? 'active' : ''}`}
              onDragEnter={handleVideoDrag}
              onDragLeave={handleVideoDrag}
              onDragOver={handleVideoDrag}
              onDrop={handleVideoDrop}
              onClick={() => document.getElementById('video-file-input')?.click()}
            >
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>🎥</p>
              <p>Drag and drop videos here, or click to select</p>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                MP4, MOV, AVI, MKV formats supported • Max 20 videos
              </p>
            </div>
            <input
              id="video-file-input"
              type="file"
              multiple
              onChange={handleVideoFileInput}
              style={{ display: 'none' }}
              accept=".mp4,.mov,.avi,.mkv,video/*"
            />

            {videos.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Videos ({videos.length})</h4>
                {videos.map((video, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  backgroundColor: video.status === 'error' ? '#fff5f5' : '#fff'
                }}
              >
                {/* Video Preview */}
                {video.previewUrl && (
                  <div
                    onClick={() => setPreviewingVideoIndex(index)}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: '1px solid #e0e0e0',
                      backgroundColor: '#000',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <video
                      src={video.previewUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      muted
                    />
                    {/* Play icon overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '30px',
                      height: '30px',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    {video.file.name} • {(video.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                    <input
                      type="text"
                      value={video.title}
                      onChange={(e) => updateVideoTitle(index, e.target.value)}
                      placeholder="Title"
                      disabled={video.status === 'uploading' || video.status === 'success'}
                      style={{ fontSize: '14px' }}
                    />
                    <select
                      value={video.locale}
                      onChange={(e) => updateVideoLocale(index, e.target.value)}
                      disabled={video.status === 'uploading' || video.status === 'success'}
                      style={{ fontSize: '14px' }}
                    >
                      {(['en', 'hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'] as const).map(lang => (
                        <option key={lang} value={lang}>{LANGUAGE_NAMES[lang]}</option>
                      ))}
                    </select>
                  </div>
                  {!video.error && video.status !== 'uploading' && video.status !== 'success' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingVideoIndex(index)}
                      style={{ fontSize: '12px', padding: '6px 10px', marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="3" y1="9" x2="21" y2="9"></line>
                      </svg>
                      Add Band
                    </button>
                  )}
                  {video.error && (
                    <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px' }}>
                      ❌ {video.error}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={`badge badge-${
                    video.status === 'success' ? 'success' :
                    video.status === 'error' ? 'error' :
                    video.status === 'uploading' ? 'warning' : 'success'
                  }`}>
                    {video.status}
                  </span>
                  {video.status !== 'uploading' && video.status !== 'success' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => removeVideo(index)}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload & Publish Button */}
      {(images.length > 0 || videos.length > 0) && (
        <div className="card" style={{ backgroundColor: '#f0f7ff' }}>
          <h3>3. Upload & Publish</h3>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            Upload all templates, auto-generate thumbnails, then publish immediately or schedule for later.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-primary"
              onClick={handleUploadAndPublish}
              disabled={uploading || (images.filter(img => img.status === 'pending').length === 0 && videos.filter(vid => vid.status === 'pending').length === 0)}
              style={{ fontSize: '16px', padding: '14px 28px' }}
            >
              {uploading
                ? '⏳ Uploading & Publishing...'
                : `🚀 Upload & Publish Now`}
            </button>
            <button
              className="btn btn-secondary"
              onClick={clearAll}
              disabled={uploading}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Text Band Editor Modal for Images */}
      {editingImageIndex !== null && images[editingImageIndex] && (
        <TextBandEditor
          imageFile={images[editingImageIndex].file}
          onTextOverlayComplete={handleTextOverlayComplete}
          onCancel={handleCancelTextEditor}
        />
      )}

      {/* Text Band Editor Modal for Videos */}
      {editingVideoIndex !== null && videos[editingVideoIndex] && (
        <TextBandEditor
          imageFile={videos[editingVideoIndex].file}
          onTextOverlayComplete={handleVideoTextOverlayComplete}
          onCancel={handleCancelVideoTextEditor}
        />
      )}

      {/* Video Preview Modal */}
      {previewingVideoIndex !== null && videos[previewingVideoIndex] && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setPreviewingVideoIndex(null)}
        >
          <div
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setPreviewingVideoIndex(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '32px',
                cursor: 'pointer',
                padding: '0',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            {/* Video info */}
            <div style={{
              color: 'white',
              marginBottom: '10px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {videos[previewingVideoIndex].title}
              </div>
              <div style={{ opacity: 0.7, fontSize: '13px' }}>
                {videos[previewingVideoIndex].file.name} • {(videos[previewingVideoIndex].file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>

            {/* Video player */}
            <video
              src={videos[previewingVideoIndex].previewUrl}
              controls
              autoPlay
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(90vh - 100px)',
                borderRadius: '8px'
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
};
